import { useMemo, useState } from "react";
import { ContextSubnav } from "./components/ContextSubnav.jsx";
import { DomainWorkspace } from "./components/DomainWorkspace.jsx";
import { EvidenceRail } from "./components/EvidenceRail.jsx";
import { PrimarySidebar } from "./components/PrimarySidebar.jsx";
import { ReceiptActions } from "./components/ReceiptActions.jsx";
import { domains } from "./domain/adapters.js";
import { buildDecisionReceipt } from "./kernel/decisionKernel.js";
import {
  appendDecisionLedgerEntry,
  buildDecisionLedgerEntry,
  readDecisionLedger,
  summarizeDecisionLedger,
  writeDecisionLedger
} from "./kernel/decisionReceiptLedger.js";
import { buildDecisionExportPacket, downloadTextFile, receiptToCsv } from "./kernel/receiptExport.js";
import { contextualViewForId, primaryItemForId } from "./knowledge/navigationModel.js";
import "./styles/app.css";

function cloneOptions(options) {
  return options.map((option) => ({
    ...option,
    variables: { ...option.variables },
    gates: option.gates.map((gate) => ({ ...gate })),
    evidence: [...option.evidence],
    missingEvidence: [...option.missingEvidence]
  }));
}

export default function App() {
  const [domainId, setDomainId] = useState("executive");
  const [activePrimaryId, setActivePrimaryId] = useState("executive");
  const [activeViewId, setActiveViewId] = useState("decision");
  const currentDomain = domains.find((domain) => domain.id === domainId) ?? domains[0];
  const activePrimary = primaryItemForId(activePrimaryId);
  const activeView = contextualViewForId(activeViewId);
  const [optionsByDomain, setOptionsByDomain] = useState(() =>
    Object.fromEntries(domains.map((domain) => [domain.id, cloneOptions(domain.options)]))
  );
  const [selectedOptionId, setSelectedOptionId] = useState("enterprise-pilot");
  const [exportStatus, setExportStatus] = useState("Receipt not exported");
  const [ledgerEntries, setLedgerEntries] = useState(() => readDecisionLedger());

  const options = optionsByDomain[currentDomain.id] ?? cloneOptions(currentDomain.options);
  const receipt = useMemo(
    () =>
      buildDecisionReceipt({
        domain: currentDomain.label,
        objective: currentDomain.objective,
        options,
        weights: currentDomain.weights
      }),
    [currentDomain, options]
  );

  const selectedOption =
    receipt.ranked.find((option) => option.id === selectedOptionId) ?? receipt.ranked[0];
  const editableOption = options.find((option) => option.id === selectedOption.id);

  function selectPrimary(nextPrimary) {
    setActivePrimaryId(nextPrimary.id);
    setActiveViewId(nextPrimary.defaultView);
    if (nextPrimary.type === "domain") {
      const nextDomain = domains.find((domain) => domain.id === nextPrimary.domainId) ?? domains[0];
      setDomainId(nextDomain.id);
      setSelectedOptionId(nextDomain.options[0].id);
    }
  }

  function updateVariable(key, value) {
    setOptionsByDomain((current) => ({
      ...current,
      [currentDomain.id]: current[currentDomain.id].map((option) =>
        option.id === selectedOption.id
          ? { ...option, variables: { ...option.variables, [key]: value } }
          : option
      )
    }));
  }

  function exportReceiptJson() {
    const packet = buildDecisionExportPacket({
      receipt,
      domain: currentDomain,
      selectedOption
    });
    const downloaded = downloadTextFile({
      filename: `strategy-${currentDomain.id}-decision-receipt.json`,
      mimeType: "application/json",
      content: `${JSON.stringify(packet, null, 2)}\n`
    });
    setExportStatus(downloaded ? "JSON receipt exported" : "JSON receipt prepared");
  }

  function exportReceiptCsv() {
    const downloaded = downloadTextFile({
      filename: `strategy-${currentDomain.id}-decision-receipt.csv`,
      mimeType: "text/csv",
      content: `${receiptToCsv(receipt)}\n`
    });
    setExportStatus(downloaded ? "CSV receipt exported" : "CSV receipt prepared");
  }

  function saveReceiptToLedger() {
    const entry = buildDecisionLedgerEntry({
      receipt,
      domain: currentDomain,
      selectedOption
    });
    const nextEntries = appendDecisionLedgerEntry(entry, ledgerEntries);
    const saved = writeDecisionLedger(nextEntries);
    setLedgerEntries(saved);
    setExportStatus("Receipt saved locally");
  }

  const ledgerSummary = summarizeDecisionLedger(ledgerEntries);

  return (
    <div className="app-shell">
      <PrimarySidebar activePrimaryId={activePrimaryId} onSelect={selectPrimary} />

      <main className="workspace">
        <header className="topbar">
          <div>
            <span className="crumb">Strategy / {activeView.label}</span>
            <h1>{activePrimary.label}</h1>
          </div>
          <ReceiptActions
            onSave={saveReceiptToLedger}
            onExportJson={exportReceiptJson}
            onExportCsv={exportReceiptCsv}
            exportStatus={exportStatus}
          />
        </header>

        <ContextSubnav
          activePrimary={activePrimary}
          activeViewId={activeViewId}
          onSelectView={setActiveViewId}
        />

        <DomainWorkspace
          activePrimary={activePrimary}
          activeViewId={activeViewId}
          currentDomain={currentDomain}
          receipt={receipt}
          selectedOption={selectedOption}
          editableOption={editableOption}
          setSelectedOptionId={setSelectedOptionId}
          updateVariable={updateVariable}
          localLedgerEntries={ledgerEntries}
          localLedgerSummary={ledgerSummary}
        />
      </main>

      <EvidenceRail
        receipt={receipt}
        selectedOption={selectedOption}
        localLedgerEntries={ledgerEntries}
        localLedgerSummary={ledgerSummary}
      />
    </div>
  );
}
